import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Flag, History } from "lucide-react";
import { toast } from "sonner";
import { asArray, errMsg, http } from "@/lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "");

export function ReportsAudit() {
  const [reports, setReports] = useState(null);
  const [audit, setAudit] = useState(null);
  const load = useCallback(() => {
    http.get("/admin/reports").then(({ data }) => setReports(asArray(data))).catch((e) => { setReports([]); toast.error(errMsg(e)); });
    http.get("/admin/audit").then(({ data }) => setAudit(asArray(data))).catch(() => setAudit([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const resolve = async (r) => {
    try { await http.post(`/admin/reports/${r.id}/resolve`); toast.success("Marked as resolved"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2" data-testid="admin-reports-audit">
      <section className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid="admin-reports">
        <p className="flex items-center gap-2 font-display text-lg font-bold text-ph-ink"><Flag className="h-4 w-4 text-ph-accent" /> Visitor reports</p>
        <p className="text-sm text-ph-muted">Outdated or broken price lists reported from the site. Each report is also emailed to you.</p>
        {reports === null ? <div className="ph2-shimmer mt-4 h-24 rounded-2xl" /> : reports.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-ph-line py-10 text-center text-sm text-ph-muted" data-testid="admin-reports-empty">No reports yet.</p>
        ) : (
          <ul className="mt-4 divide-y divide-ph-line">
            {reports.map((r) => (
              <li key={r.id} className="flex items-start gap-3 py-3" data-testid={`admin-report-${r.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ph-ink">{r.reason} <span className={`ml-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${r.status === "open" ? "bg-amber-500/15 text-amber-700 dark:text-amber-300" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300"}`}>{r.status}</span></p>
                  <p className="text-xs text-ph-muted"><Link to={`/brands/${r.brand_slug}`} className="underline">{r.brand_name} — {r.doc_title}</Link> · {when(r.created_at)}{r.email ? ` · ${r.email}` : ""}</p>
                  {r.note && <p className="mt-1 text-sm text-ph-ink/85">{r.note}</p>}
                </div>
                {r.status === "open" && (
                  <button type="button" onClick={() => resolve(r)} data-testid={`admin-report-resolve-${r.id}`} className="inline-flex shrink-0 items-center gap-1 rounded-full border border-ph-line px-3 py-1.5 text-xs font-semibold text-ph-ink hover:bg-ph-raised">
                    <Check className="h-3.5 w-3.5" /> Resolve
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="rounded-3xl border border-ph-line bg-ph-surface p-6" data-testid="admin-audit">
        <p className="flex items-center gap-2 font-display text-lg font-bold text-ph-ink"><History className="h-4 w-4 text-ph-accent" /> Audit history</p>
        <p className="text-sm text-ph-muted">Uploads, edits, publishing, deletions and file checks by admins (latest 200).</p>
        {audit === null ? <div className="ph2-shimmer mt-4 h-24 rounded-2xl" /> : audit.length === 0 ? (
          <p className="mt-4 rounded-2xl border border-dashed border-ph-line py-10 text-center text-sm text-ph-muted" data-testid="admin-audit-empty">No admin actions recorded yet.</p>
        ) : (
          <ul className="mt-4 max-h-[420px] divide-y divide-ph-line overflow-y-auto" data-lenis-prevent>
            {audit.map((a, i) => (
              <li key={`${a.at}-${i}`} className="py-2.5 text-sm">
                <span className="font-semibold capitalize text-ph-ink">{a.action}</span> <span className="text-ph-ink/85">{a.target}</span>
                <span className="block text-xs text-ph-muted">{a.user} · {when(a.at)}{a.detail ? ` · ${a.detail}` : ""}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
